
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ChartInfoShort} from './ChartInfoShort';
import {FieldModelChartInfoShort} from './ChartInfoShort';

/**
 * ChartInfoFull struct
 */
class ChartInfoFull {
  /**
   * Initialize struct
   * @param {!ChartInfoShort=} chartInfoShort
   * @param {!string=} chartSettings
   * @constructor
   */
  constructor (chartInfoShort = new ChartInfoShort(), chartSettings = '') {
    this.ChartInfoShort = chartInfoShort
    this.ChartSettings = chartSettings
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ChartInfoFull}
   * @param {!ChartInfoFull} other Other struct
   * @returns {!ChartInfoFull} This struct
   */
  copy (other) {
    if (other.ChartInfoShort != null) {
      this.ChartInfoShort = ChartInfoShort.fromObject(other.ChartInfoShort)
    } else {
      this.ChartInfoShort = null
    }
    if (other.ChartSettings != null) {
      this.ChartSettings = other.ChartSettings
    } else {
      this.ChartSettings = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ChartInfoFull}
   * @returns {!ChartInfoFull} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ChartInfoFullModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ChartInfoFullModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ChartInfoFull}
   * @param {!ChartInfoFull} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ChartInfoFull)) {
      throw new TypeError('Instance of ChartInfoFull is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ChartInfoFull}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      ChartInfoShort: ((this.ChartInfoShort != null) ? this.ChartInfoShort : null),
      ChartSettings: ((this.ChartSettings != null) ? this.ChartSettings : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ChartInfoFull.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ChartInfoFull} other Object value
   * @returns {!ChartInfoFull} Created struct
   */
  static fromObject (other) {
    return new ChartInfoFull().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ChartInfoFull}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ChartInfoFull.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ChartInfoFull}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5190
  }
}

export { ChartInfoFull };

/**
 * Fast Binary Encoding ChartInfoFull field model
 */
class FieldModelChartInfoFull extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.ChartInfoShort = new FieldModelChartInfoShort(buffer, 4 + 4)
    this.ChartSettings = new fbe.FieldModelString(buffer, this.ChartInfoShort.fbeOffset + this.ChartInfoShort.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelChartInfoFull}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelChartInfoFull}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.ChartInfoShort.fbeSize + this.ChartSettings.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelChartInfoFull}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.ChartInfoShort.fbeExtra + this.ChartSettings.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelChartInfoFull}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelChartInfoFull.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelChartInfoFull}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5190
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelChartInfoFull}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelChartInfoFull}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.ChartInfoShort.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ChartInfoShort.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartInfoShort.fbeSize

    if ((fbeCurrentSize + this.ChartSettings.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ChartSettings.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartSettings.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelChartInfoFull}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelChartInfoFull}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelChartInfoFull}
   * @param {!ChartInfoFull} fbeValue Default value, defaults is new ChartInfoFull()
   * @returns {!ChartInfoFull} ChartInfoFull value
   */
  get (fbeValue = new ChartInfoFull()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelChartInfoFull}
   * @param {!ChartInfoFull} fbeValue ChartInfoFull value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.ChartInfoShort.fbeSize) <= fbeStructSize) {
      fbeValue.ChartInfoShort = this.ChartInfoShort.get()
    } else {
      fbeValue.ChartInfoShort = new ChartInfoShort()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartInfoShort.fbeSize

    if ((fbeCurrentSize + this.ChartSettings.fbeSize) <= fbeStructSize) {
      fbeValue.ChartSettings = this.ChartSettings.get()
    } else {
      fbeValue.ChartSettings = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartSettings.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelChartInfoFull}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelChartInfoFull}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelChartInfoFull}
   * @param {!ChartInfoFull} fbeValue ChartInfoFull value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelChartInfoFull}
   * @param {ChartInfoFull} fbeValue ChartInfoFull value
   */
  setFields (fbeValue) {
    this.ChartInfoShort.set(fbeValue.ChartInfoShort)
    this.ChartSettings.set(fbeValue.ChartSettings)
  }
}

export { FieldModelChartInfoFull };

/**
 * Fast Binary Encoding ChartInfoFull model
 */
class ChartInfoFullModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelChartInfoFull(this.buffer, 4)
  }

  /**
   * Get the ChartInfoFull model
   * @this {!ChartInfoFullModel}
   * @returns {!FieldModelChartInfoFull} model ChartInfoFull model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ChartInfoFullModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ChartInfoFullModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ChartInfoFullModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ChartInfoFullModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelChartInfoFull.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ChartInfoFullModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ChartInfoFullModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ChartInfoFullModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ChartInfoFullModel}
   * @param {!ChartInfoFull} value ChartInfoFull value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ChartInfoFullModel}
   * @param {!ChartInfoFull} value ChartInfoFull value, defaults is new ChartInfoFull()
   * @return {!object} Deserialized ChartInfoFull value and its size
   */
  deserialize (value = new ChartInfoFull()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ChartInfoFull(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ChartInfoFull(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ChartInfoFullModel}
   * @param {!number} prev Previous ChartInfoFull model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ChartInfoFullModel };
